import { Input } from "@mantine/core"
import { Popover } from "@shopify/polaris"
import React, { useContext, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { CoolButton } from "../CoolButton.jsx"
import { GlowingButtonContainer } from "../GlowingButtonContainer.jsx"
import { AIStateContext } from "../providers/AIStateProvider.jsx"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"
import { useTranslation } from "react-i18next"
import { LANGUAGE_CODES, getLanguageName } from "../../utils/languages.js"

export const TranslateButton = ({ onLanguageSelect, language, disabled }) => {
	const { state, dispatch } = useContext(GlobalStateContext)
	const { aiState, setAIState } = useContext(AIStateContext)
	const { t, i18n } = useTranslation()

	const [translatePopoverActive, setTranslatePopoverActive] = useState(false)
	const [languageSearchInput, setLanguageSearchInput] = useState("")

	const toggleTranslatePopover = (e) => {
		e.stopPropagation()
		setTranslatePopoverActive(!translatePopoverActive)
	}

	const changeSelectedLanguage = (lang) => {
		onLanguageSelect(lang)
		setTranslatePopoverActive(false)
	}

	const filterLanguageCodes = (languageCodes, searchInput) => {
		if (!searchInput) return languageCodes

		return languageCodes.filter((lang) => {
			return LANGUAGE_CODES[lang]
				.toLowerCase()
				.includes(searchInput.toLowerCase())
		})
	}

	useEffect(() => {
		if (state.storeLanguage || state.defaultTranslateLanguage) {
			onLanguageSelect(
				state.defaultTranslateLanguage || state.storeLanguage,
			)
		}
	}, [state.storeLanguage, state.defaultTranslateLanguage])

	const languageCodes = Object.keys(LANGUAGE_CODES)
	const filteredLanguageCodes = useMemo(
		() => filterLanguageCodes(languageCodes, languageSearchInput),
		[languageCodes, languageSearchInput],
	)

	return (
		<>
			<div
				style={{
					background: "#c2e1ff",
					color: "#154cac",
					borderRadius: "0.3rem",
					display: "flex",
					gap: "0.2rem",
					fontSize: "0.75rem",
					cursor: "pointer",
					width: "fit-content",
				}}
				onClick={(e) => {
					if (!disabled) {
						toggleTranslatePopover(e)
					}
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						padding: "0.1rem 0.3rem",
						paddingRight: "0.1rem",
						borderRadius: "0.3rem 0rem 0rem 0.3rem",
					}}
				>
					<svg width={"0.9rem"} height={"0.9rem"} viewBox="0 0 32 32">
						<path
							fill="currentColor"
							d="M27.85 29H30l-6-15h-2.35l-6 15h2.15l1.6-4h6.85zm-7.65-6l2.62-6.56L25.45 23zM18 7V5h-7V2H9v3H2v2h10.74a14.71 14.71 0 0 1-3.19 6.18A13.5 13.5 0 0 1 7.26 9h-2.1a16.47 16.47 0 0 0 3 5.58A16.84 16.84 0 0 1 3 18l.75 1.86A18.47 18.47 0 0 0 9.53 16a16.92 16.92 0 0 0 5.76 3.84L16 18a14.48 14.48 0 0 1-5.12-3.37A17.64 17.64 0 0 0 14.8 7z"
						></path>
					</svg>
				</div>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.2rem",
						padding: "0.1rem 0.3rem",
						paddingLeft: "0rem",
					}}
				>
					<span>
						{t("ai.page_language")}:{" "}
						<span
							style={{
								fontWeight: "600",
								textTransform: "capitalize",
							}}
						>
							{getLanguageName(language, i18n.language)}
						</span>
					</span>
					<div
						className="language-selector-icon"
						style={{ height: "0.85rem", width: "0.85rem" }}
					>
						<svg
							width={"0.9rem"}
							height={"0.9rem"}
							viewBox="0 0 512 512"
						>
							<path
								d="M128 192l128 128 128-128z"
								fill="currentColor"
							></path>
						</svg>
					</div>
				</div>
			</div>

			<Popover
				active={translatePopoverActive}
				activator={<div></div>}
				onClose={() => setTranslatePopoverActive(false)}
			>
				<div
					style={{
						padding: "0.5rem",
						height: "12rem",
						width: "9rem",
						overflowY: "scroll",
					}}
				>
					<p style={{ fontSize: "0.7rem", marginBottom: "0.5rem" }}>
						{t("ai.your_store_languages")}
					</p>

					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "0.1rem",
						}}
					>
						{state.availableStoreLanguages.map((lang) => {
							return (
								<div
									key={lang}
									onClick={() => changeSelectedLanguage(lang)}
									className="language-button"
								>
									<svg
										width="0.85rem"
										height="0.85rem"
										viewBox="0 0 32 32"
									>
										<path
											fill="currentColor"
											d="M27.85 29H30l-6-15h-2.35l-6 15h2.15l1.6-4h6.85zm-7.65-6l2.62-6.56L25.45 23zM18 7V5h-7V2H9v3H2v2h10.74a14.71 14.71 0 0 1-3.19 6.18A13.5 13.5 0 0 1 7.26 9h-2.1a16.47 16.47 0 0 0 3 5.58A16.84 16.84 0 0 1 3 18l.75 1.86A18.47 18.47 0 0 0 9.53 16a16.92 16.92 0 0 0 5.76 3.84L16 18a14.48 14.48 0 0 1-5.12-3.37A17.64 17.64 0 0 0 14.8 7z"
										></path>
									</svg>
									<span
										style={{
											fontSize: "0.85rem",
											textTransform: "capitalize",
										}}
									>
										{getLanguageName(lang, i18n.language)}
									</span>
								</div>
							)
						})}
					</div>

					<div
						style={{
							borderTop: "1px solid #ccc",
							paddingTop: "1rem",
							marginTop: "0.5rem",
						}}
					>
						<p
							style={{
								fontSize: "0.7rem",
								marginBottom: "0.5rem",
							}}
						>
							{t("ai.all_languages")}
						</p>

						<Input
							variant="filled"
							size="xs"
							radius="md"
							placeholder={t("ai.search_language")}
							value={languageSearchInput}
							onChange={(e) =>
								setLanguageSearchInput(e.target.value)
							}
						/>

						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: "0.1rem",
								marginTop: "0.5rem",
							}}
						>
							{filteredLanguageCodes.map((lang) => {
								return (
									<div
										key={lang}
										onClick={() =>
											changeSelectedLanguage(lang)
										}
										className="language-button"
									>
										<svg
											width="0.85rem"
											height="0.85rem"
											viewBox="0 0 32 32"
										>
											<path
												fill="currentColor"
												d="M27.85 29H30l-6-15h-2.35l-6 15h2.15l1.6-4h6.85zm-7.65-6l2.62-6.56L25.45 23zM18 7V5h-7V2H9v3H2v2h10.74a14.71 14.71 0 0 1-3.19 6.18A13.5 13.5 0 0 1 7.26 9h-2.1a16.47 16.47 0 0 0 3 5.58A16.84 16.84 0 0 1 3 18l.75 1.86A18.47 18.47 0 0 0 9.53 16a16.92 16.92 0 0 0 5.76 3.84L16 18a14.48 14.48 0 0 1-5.12-3.37A17.64 17.64 0 0 0 14.8 7z"
											></path>
										</svg>
										<span
											style={{
												fontSize: "0.85rem",
												textTransform: "capitalize",
											}}
										>
											{getLanguageName(
												lang,
												i18n.language,
											)}
										</span>
									</div>
								)
							})}
						</div>
					</div>
				</div>
			</Popover>

			<style>{`
				.ai-orders-buttons-strip {
					filter: opacity(0.6) blur(1.5px);
					transition: filter 0.2s ease-in-out;
				}
				.ai-orders-buttons-strip:hover {
					filter: opacity(0.5) blur(0px);
				}

				.language-button {
					display: flex;
					gap: 0.3rem;
					align-items: center;
					padding: 0.2rem 0.3rem;
					cursor: pointer;
					background: none;
					border-radius: 0.3rem;
				}
				.language-button:hover {
					background: #e8e8e8;
				}
				.language-selector-icon:hover {
					border-radius: 0.25rem;
					background: #92bde7;
				}
			`}</style>
		</>
	)
}
